/**
 * Synthetic field graph for the event planner motion sim.
 *
 * KAGS has no authored taxiways / runway / exit points yet, so we synthesize a
 * believable layout from the apron bounding box plus a chosen runway bearing:
 * a runway offset to one side, 2–3 exit gates along the apron's runway-facing
 * edge, and per-zone anchors (nearest gate). Departures flow toward a shared
 * gate then down the runway — the "exodus drain" look.
 *
 * `ExitGeometry` matches the shape hand-off A introduces, so when real exit
 * assignment lands we swap synthetic gates for routed ones with no plan changes.
 */
import type { ZoneGeometry } from '../engine/types'
import { bearingDeg, headingToForward } from '../motion/easing'
import { resolveActiveRunway, resolveNetworkAt, type NodeRole, type TaxiNode, type TaxiPlan } from './taxiGraph'

/** A taxiway exit / gate the field drains toward. Shared shape with hand-off A. */
export interface ExitGeometry {
  id: string
  name: string
  /** scene feet [x, z] */
  center: [number, number]
  /** taxi heading through the gate, degrees */
  headingDeg: number
  /** aircraft per departure slot this gate can push (advisory) */
  throughputPerSlot: number
}

export interface FieldGraph {
  /** legacy single bearing; kept = `approachBearingDeg` for back-compat. */
  runwayBearingDeg: number
  /** heading flown on final + landing rollout, degrees. */
  approachBearingDeg: number
  /** heading on the takeoff roll + climb-out, degrees. */
  departureBearingDeg: number
  /** approach-end threshold (arrivals touch down here), scene feet [x, z] */
  thresholdApproach: [number, number]
  /** departure-end threshold (takeoff roll starts here), scene feet [x, z] */
  thresholdDeparture: [number, number]
  /** airborne final entry (before the approach threshold), scene feet [x, z] */
  finalEntry: [number, number]
  /** touchdown point just past the approach threshold (rollout begins here), scene feet [x, z] */
  touchdownPoint: [number, number]
  /** where the landing rollout decelerates to before exiting onto a taxiway, scene feet [x, z] */
  rolloutEnd: [number, number]
  /** takeoff: where rotation / liftoff begins along the runway, scene feet [x, z] */
  rotationPoint: [number, number]
  /** airborne climb-out exit (after the departure threshold), scene feet [x, z] */
  climbExit: [number, number]
  /**
   * Shared taxiway centerline parallel to the runway, between the apron and the
   * runway. Ground routes merge onto this spine instead of fanning across grass.
   * [endNearApproach, endNearDeparture], scene feet [x, z].
   */
  spine: [[number, number], [number, number]]
  /** Hold-short nodes on the runway aligned with the spine ends, scene feet [x, z]. */
  runwayAccess: [number, number][]
  gates: ExitGeometry[]
  /**
   * Fuel farm: the single depot fuel trucks dispatch from and return to. Sits on
   * the apron's far edge (away from the runway) so trucks route across the field
   * to each fueling aircraft. Scene feet [x, z].
   */
  fuelDepot: [number, number]
}

export interface FieldGraphOptions {
  runwayBearingDeg?: number
  runwayOffsetFt?: number
  runwayHalfLengthFt?: number
  finalRunFt?: number
  finalAltFt?: number
  gateCount?: number
}

/** Tunables for deriving runway-roll geometry from authored thresholds. */
export interface RunwayRollOptions {
  /** ground distance of the descending final segment, feet (default 1400). */
  finalRunFt?: number
  /** how far past the threshold the aircraft touches down, feet (default 300). */
  touchdownZoneFt?: number
  /** fraction of runway length used for the landing rollout (default 0.5). */
  rolloutFraction?: number
  /** fraction of runway length used for the takeoff roll before rotation (default 0.55). */
  takeoffRollFraction?: number
}

const perp = (headingDeg: number): [number, number] => headingToForward(headingDeg + 90)

/** Axis-aligned bounding box of all zones (centers ± half extents; rotation ignored — fine for synth). */
function zonesBBox(zones: ZoneGeometry[]): { cx: number; cz: number; halfX: number; halfZ: number } {
  if (zones.length === 0) return { cx: 0, cz: 0, halfX: 300, halfZ: 200 }
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (const z of zones) {
    const hx = z.width / 2
    const hz = z.depth / 2
    minX = Math.min(minX, z.center[0] - hx)
    maxX = Math.max(maxX, z.center[0] + hx)
    minZ = Math.min(minZ, z.center[1] - hz)
    maxZ = Math.max(maxZ, z.center[1] + hz)
  }
  return { cx: (minX + maxX) / 2, cz: (minZ + maxZ) / 2, halfX: (maxX - minX) / 2, halfZ: (maxZ - minZ) / 2 }
}

export function synthesizeFieldGraph(zones: ZoneGeometry[], opts: FieldGraphOptions = {}): FieldGraph {
  const runwayBearingDeg = opts.runwayBearingDeg ?? 170 // KAGS 17/35-ish
  const bbox = zonesBBox(zones)
  const apronReach = Math.max(bbox.halfX, bbox.halfZ)
  const runwayOffsetFt = opts.runwayOffsetFt ?? apronReach + 1800
  const runwayHalfLengthFt = opts.runwayHalfLengthFt ?? Math.max(2500, apronReach + 1500)
  const finalRunFt = opts.finalRunFt ?? 1400
  const gateCount = Math.max(1, opts.gateCount ?? 3)

  const fwd = headingToForward(runwayBearingDeg) // along runway
  const side = perp(runwayBearingDeg) // apron → runway direction

  const center: [number, number] = [bbox.cx, bbox.cz]
  const runwayCenter: [number, number] = [
    center[0] + side[0] * runwayOffsetFt,
    center[1] + side[1] * runwayOffsetFt,
  ]
  const thresholdApproach: [number, number] = [
    runwayCenter[0] - fwd[0] * runwayHalfLengthFt,
    runwayCenter[1] - fwd[1] * runwayHalfLengthFt,
  ]
  const thresholdDeparture: [number, number] = [
    runwayCenter[0] + fwd[0] * runwayHalfLengthFt,
    runwayCenter[1] + fwd[1] * runwayHalfLengthFt,
  ]
  const finalEntry: [number, number] = [
    thresholdApproach[0] - fwd[0] * finalRunFt,
    thresholdApproach[1] - fwd[1] * finalRunFt,
  ]
  const climbExit: [number, number] = [
    thresholdDeparture[0] + fwd[0] * finalRunFt,
    thresholdDeparture[1] + fwd[1] * finalRunFt,
  ]

  // Taxiway spine: parallel to the runway, just outside the apron's runway-facing
  // edge (well short of the runway). All ground routes merge onto it.
  const spineOffsetFt = apronReach + 400
  const spineHalfLen = runwayHalfLengthFt
  const spineCenter: [number, number] = [
    center[0] + side[0] * spineOffsetFt,
    center[1] + side[1] * spineOffsetFt,
  ]
  const spine: [[number, number], [number, number]] = [
    [spineCenter[0] - fwd[0] * spineHalfLen, spineCenter[1] - fwd[1] * spineHalfLen],
    [spineCenter[0] + fwd[0] * spineHalfLen, spineCenter[1] + fwd[1] * spineHalfLen],
  ]
  // Hold-short nodes on the runway aligned with the spine ends.
  const runwayAccess: [number, number][] = [
    [runwayCenter[0] - fwd[0] * spineHalfLen, runwayCenter[1] - fwd[1] * spineHalfLen],
    [runwayCenter[0] + fwd[0] * spineHalfLen, runwayCenter[1] + fwd[1] * spineHalfLen],
  ]

  // Gates sit ON the spine, spread along the runway axis (used for tug staging
  // + handoff and as visual reference). Positioning them on the taxiway keeps
  // tow staging on pavement instead of the grass.
  const spread = Math.max(bbox.halfX, bbox.halfZ)
  const gates: ExitGeometry[] = []
  for (let i = 0; i < gateCount; i++) {
    // -1, 0, +1 ... offsets along the runway axis
    const t = gateCount === 1 ? 0 : (i / (gateCount - 1)) * 2 - 1
    const along = t * spread
    gates.push({
      id: `synth-gate-${i + 1}`,
      name: `Gate ${i + 1}`,
      center: [spineCenter[0] + fwd[0] * along, spineCenter[1] + fwd[1] * along],
      headingDeg: runwayBearingDeg + 90, // taxi toward the runway
      throughputPerSlot: 4,
    })
  }

  // Fuel farm on the apron's far edge (opposite the runway), toward one end so
  // trucks make a visible cross-apron run to each fueling aircraft.
  const fuelDepot: [number, number] = [
    center[0] - side[0] * (apronReach + 300) + fwd[0] * spread * 0.7,
    center[1] - side[1] * (apronReach + 300) + fwd[1] * spread * 0.7,
  ]

  return {
    runwayBearingDeg,
    approachBearingDeg: runwayBearingDeg,
    departureBearingDeg: runwayBearingDeg,
    thresholdApproach,
    thresholdDeparture,
    finalEntry,
    // No on-runway roll for the synth graph — roll points collapse onto the
    // thresholds, so buildMovementPlan skips the (zero-length) roll legs and the
    // legacy "threshold → climb" behavior is preserved. Real roll geometry comes
    // from `fieldGraphFromPlan` when an authored runway exists.
    touchdownPoint: thresholdApproach,
    rolloutEnd: thresholdApproach,
    rotationPoint: thresholdDeparture,
    climbExit,
    spine,
    runwayAccess,
    gates,
    fuelDepot,
  }
}

const unitVec = (a: readonly [number, number], b: readonly [number, number]): [number, number] => {
  const dx = b[0] - a[0]
  const dz = b[1] - a[1]
  const len = Math.hypot(dx, dz)
  return len < 1e-6 ? [0, 0] : [dx / len, dz / len]
}

/**
 * Tug-staging gates from the authored network's live nodes at `atMs`. Prefer
 * explicit `gate` nodes, then runway exits, then apron nodes — so staging sits
 * on real pavement instead of the synthetic bbox-offset gates (which can land
 * off the field, over the car park). Returns null when the plan has none, so the
 * caller keeps the synthetic gates.
 */
function gatesFromPlan(plan: TaxiPlan, atMs: number, fallbackHeadingDeg: number): ExitGeometry[] | null {
  const { nodes } = resolveNetworkAt(plan, atMs)
  const tiers: NodeRole[] = ['gate', 'exit_to_taxiway', 'apron']
  let picked: TaxiNode[] = []
  for (const role of tiers) {
    picked = nodes.filter((n) => n.role === role)
    if (picked.length > 0) break
  }
  if (picked.length === 0) return null
  return picked.map((n, i) => ({
    id: n.id,
    name: (n.flags?.name as string) ?? `Gate ${i + 1}`,
    center: [n.pos[0], n.pos[1]],
    headingDeg: n.headingDeg ?? fallbackHeadingDeg,
    throughputPerSlot: n.throughputPerSlot ?? 4,
  }))
}

/**
 * Derive a `FieldGraph` from the active authored runway in `plan` at `atMs`.
 * Picks the configured landing / takeoff ends to orient the airborne legs and
 * the on-runway roll geometry (touchdown → rollout, takeoff roll → rotation).
 * Falls back to `fallback` (the synth/aeroway graph) when no runway is live, so
 * spine/gates (apron-relative) are always inherited.
 */
export function fieldGraphFromPlan(
  plan: TaxiPlan,
  atMs: number,
  fallback: FieldGraph,
  opts: RunwayRollOptions = {}
): FieldGraph {
  // Tug staging always prefers authored nodes when present, regardless of whether
  // a runway is live (the synthetic gates are bbox-derived and can land off-field).
  const authoredGates = gatesFromPlan(plan, atMs, fallback.runwayBearingDeg)
  const withGates = authoredGates ? { ...fallback, gates: authoredGates } : fallback

  const rwy = resolveActiveRunway(plan, atMs)
  if (!rwy) return withGates

  const finalRunFt = opts.finalRunFt ?? 1400
  const touchdownZoneFt = opts.touchdownZoneFt ?? 300
  const rolloutFraction = opts.rolloutFraction ?? 0.5
  const takeoffRollFraction = opts.takeoffRollFraction ?? 0.55

  const [e0, e1] = rwy.ends
  const landIdx = e0.useForLanding ? 0 : e1.useForLanding ? 1 : 0
  const depIdx = e0.useForTakeoff ? 0 : e1.useForTakeoff ? 1 : landIdx === 0 ? 1 : 0
  const landEnd = rwy.ends[landIdx]
  const landOpp = rwy.ends[landIdx === 0 ? 1 : 0]
  const depEnd = rwy.ends[depIdx]
  const depOpp = rwy.ends[depIdx === 0 ? 1 : 0]

  const thresholdApproach: [number, number] = [landEnd.threshold[0], landEnd.threshold[1]]
  const thresholdDeparture: [number, number] = [depEnd.threshold[0], depEnd.threshold[1]]
  const runwayLen = Math.hypot(e1.threshold[0] - e0.threshold[0], e1.threshold[1] - e0.threshold[1]) || 1

  // Forward = direction of travel down the runway from each in-use threshold.
  const approachFwd = unitVec(landEnd.threshold, landOpp.threshold)
  const departureFwd = unitVec(depEnd.threshold, depOpp.threshold)
  const approachBearingDeg = bearingDeg(landEnd.threshold, landOpp.threshold)
  const departureBearingDeg = bearingDeg(depEnd.threshold, depOpp.threshold)

  const along = (base: readonly [number, number], dir: readonly [number, number], d: number): [number, number] => [
    base[0] + dir[0] * d,
    base[1] + dir[1] * d,
  ]

  const finalEntry = along(thresholdApproach, approachFwd, -finalRunFt)
  const touchdownPoint = along(thresholdApproach, approachFwd, touchdownZoneFt)
  const rolloutEnd = along(touchdownPoint, approachFwd, Math.min(runwayLen * rolloutFraction, runwayLen - touchdownZoneFt))
  const rotationPoint = along(thresholdDeparture, departureFwd, runwayLen * takeoffRollFraction)
  const climbExit = along(thresholdDeparture, departureFwd, runwayLen + finalRunFt)

  return {
    ...withGates,
    runwayBearingDeg: approachBearingDeg,
    approachBearingDeg,
    departureBearingDeg,
    thresholdApproach,
    thresholdDeparture,
    finalEntry,
    touchdownPoint,
    rolloutEnd,
    rotationPoint,
    climbExit,
    runwayAccess: [thresholdApproach, thresholdDeparture],
  }
}

/** Nearest gate to a point (scene feet). */
export function nearestGate(graph: FieldGraph, point: readonly [number, number]): ExitGeometry {
  let best = graph.gates[0]
  let bestD = Infinity
  for (const g of graph.gates) {
    const d = Math.hypot(g.center[0] - point[0], g.center[1] - point[1])
    if (d < bestD) {
      bestD = d
      best = g
    }
  }
  return best
}
